import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import remaLogo from './remalogo.svg';
import Swal from 'sweetalert2';
import CircularProgress from '@mui/material/CircularProgress';

const guidelineData = [
  {
    title: "Submodel Selection and Data Passing",
    bullets: [
      "When a user selects a submodel, only the specific information related to the chosen submodel is passed to the Dataspace asset.",
      "Users can choose a set of first-level submodels along with their respective underlying submodel element collections and properties, which will be transferred to the dataspace."
    ]
  },
  {
    title: "Persistence of Chosen Submodels",
    bullets: [
      "The selected first-level submodels are persistent for each type of asset and are common across all users of the tool.",
      "Changes made by one user to the selected submodels will reflect for all users of the service, ensuring consistency across the platform."
    ]
  },
  {
    title: "Instance-Specific Behavior",
    bullets: [
      "If a user deploys and communicates with a different instance of the service, the changes or selections of submodels will not affect other instances. Each instance maintains its own set of chosen submodels."
    ]
  },
  {
    title: "RemaNet Digital Twin and Data Space Connection",
    bullets: [
      "This guideline describes the connection between the RemaNet product digital twin and the RemaNet data space, ensuring that the appropriate submodel data flows between these components based on user-defined selections."
    ]
  }
];

function Filtering() {
  const [youDo, setYouDo] = useState([]);
  const [details, setDetails] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [syncing] = useState(false);
  const [activeSubmodels, setActiveSubmodels] = useState([]);
  const [isFetchCompleted, setIsFetchCompleted] = useState(true);
  const [openGuidelines, setOpenGuidelines] = useState([0, 1, 2, 3]);
  const navigate = useNavigate();

  const aasHost = process.env.REACT_APP_AAS_HOST;
  const syncPort = process.env.REACT_APP_SYNC_SERVICE_PORT || '3300';

  const getCategory = (asset) => asset.category ?? asset.asset?.category ?? asset.assetInformation?.assetType ?? 'Other';
  const getShellIdentifier = (asset) => asset.identification?.id ?? asset.id ?? '';

  function assetShowing() {
    fetch(`/aasServer/shells`)
      .then(response => response.json())
      .then(data => {
        const shells = Array.isArray(data) ? data : (data?.result ?? []);
        setYouDo(shells);
      });
  }

  useEffect(() => { assetShowing(); }, [syncing]);

  useEffect(() => {
    const uniqueCategories = [...new Set(youDo.map(asset => getCategory(asset)))];
    setCategories(uniqueCategories);
    setSelectedCategory(uniqueCategories[0] || '');
  }, [youDo]);

  useEffect(() => {
    const filtered = youDo.filter(instance => getCategory(instance) === selectedCategory);
    setFilteredAssets(filtered);
  }, [selectedCategory, youDo]);

  useEffect(() => {}, [activeSubmodels]);

  const [categoryToSend, setCategoryToSend] = useState("");
  const [clicked, setClicked] = useState(false);
  const [subModels, setSubModels] = useState([]);
  const [checkedSubModels, setCheckedSubModels] = useState([]);

  const clickedShowIcon = (category) => {
    setClicked(true);
    const mySelectedCategory = category || 'Other';

    fetch(`/sync-api/rertieve_submodels`, {
      method: "POST",
      body: JSON.stringify({ "type": mySelectedCategory }),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(response => response.json())
      .then(data => {
        const fetchedSubmodels = data.message;
        if (!Array.isArray(fetchedSubmodels)) {
          console.error("Unexpected response from sync service:", data);
          return;
        }
        setActiveSubmodels([...fetchedSubmodels]);
        setSubModels([...fetchedSubmodels]);
        setCheckedSubModels(() => {
          const newCheckedState = new Array(details.length).fill(false);
          fetchedSubmodels.forEach(submodel => {
            const index = details.findIndex(d => d.idShort === submodel);
            if (index !== -1) newCheckedState[index] = true;
          });
          return newCheckedState;
        });

        const assetsInCategory = youDo.filter(asset => {
          const cat = getCategory(asset)?.trim() || 'Other';
          if (mySelectedCategory === 'Other' ? cat === 'Other' : cat === mySelectedCategory) {
            setCategoryToSend(cat);
            return true;
          }
          return false;
        });

        if (assetsInCategory.length > 0) {
          setIsFetchCompleted(false);
          Promise.all(
            assetsInCategory.map(asset => {
              const shellId = encodeURIComponent(getShellIdentifier(asset));
              return fetch(`/aasServer/shells/${shellId}/aas/submodels`)
                .then(r => r.json())
                .then(d => Array.isArray(d) ? d : (d?.result ?? []))
                .catch(() => []);
            })
          ).then(results => {
            // Union of all submodels across all assets in category, deduplicated by idShort
            const seen = new Set();
            const merged = results.flat().filter(sm => {
              if (!sm?.idShort || seen.has(sm.idShort)) return false;
              seen.add(sm.idShort);
              return true;
            });
            setDetails(merged);
            setIsFetchCompleted(true);
            setCheckedSubModels(() => {
              const newCheckedState = new Array(merged.length).fill(false);
              fetchedSubmodels.forEach(submodel => {
                const index = merged.findIndex(d => d.idShort === submodel);
                if (index !== -1) newCheckedState[index] = true;
              });
              return newCheckedState;
            });
          });
        }
      })
      .catch(error => console.error("Error retrieving submodels:", error));
  };

  function handleSubmodels() {
    const payload = { "submodels": subModels, "type": categoryToSend };
    fetch(`/sync-api/submit_submodels`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(response => {
        if (response.ok) {
          Swal.fire({ title: 'Submodels for transfer successfully updated', icon: 'success', confirmButtonText: 'OK' });
        } else {
          Swal.fire({ title: 'An error occurred', icon: 'error', confirmButtonText: 'OK' });
        }
      })
      .catch(error => console.error("We encountered an error:", error));
  }

  const toggleCheckbox = (e, index) => {
    const submodelName = details[index].idShort;
    setCheckedSubModels(prev => {
      const updated = [...prev];
      updated[index] = e.target.checked;
      return updated;
    });
    if (e.target.checked) {
      setSubModels(prev => !prev.includes(submodelName) ? [...prev, submodelName] : prev);
    } else {
      setSubModels(prev => prev.filter(item => item !== submodelName));
    }
  };

  const toggleGuideline = (i) => {
    setOpenGuidelines(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
    );
  };

  const categoryCount = (cat) =>
    youDo.filter(a => getCategory(a) === cat).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", backgroundColor: "#fff", borderBottom: "1px solid #d3d3d3", height: "60px", position: "sticky", top: 0, zIndex: 1030, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "0 24px", flexShrink: 0 }}>
        <img src={remaLogo} style={{ height: "26px", display: "block" }} alt="RemaNet" />
        <div style={{ width: "1px", height: "22px", background: "#e0e0e0", flexShrink: 0 }} />
        <span style={{ fontSize: "13px", color: "#7c7c7c", fontWeight: "500", letterSpacing: "0.02em" }}>DataSpace Explorer</span>
      </div>

      {/* ── Nav tabs ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", backgroundColor: "#fff", borderBottom: "1px solid #d3d3d3", padding: "0 16px", flexShrink: 0 }}>
        <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#7c7c7c", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid transparent", marginBottom: "-1px" }} onClick={() => navigate("/")}>DT Explorer</button>
        <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#269044", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid #269044", marginBottom: "-1px" }} onClick={() => navigate("/sync")}>Product DT-DS</button>
        <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#7c7c7c", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid transparent", marginBottom: "-1px" }} onClick={() => navigate("/dataspace")}>DS Management</button>
      </div>

      {/* ── Three-column body ────────────────────────────────────────────── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden", background: "#ebebeb", gap: "1px" }}>

        {/* Left: Categories — 22% */}
        <div style={{ width: "22%", minWidth: "190px", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 10px", flexShrink: 0 }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Available Categories</span>
          </div>
          <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: "auto", padding: "10px 10px" }}>
            {categories.map(cat => {
              const isSelected = categoryToSend === cat;
              const count = categoryCount(cat);
              return (
                <div
                  key={cat}
                  onClick={() => clickedShowIcon(cat)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    marginBottom: "6px",
                    borderRadius: "8px",
                    border: isSelected ? "1.5px solid rgba(38,144,68,0.45)" : "1px solid #e4e4e4",
                    background: isSelected ? "rgba(38,144,68,0.07)" : "#fafafa",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: isSelected ? "0 1px 5px rgba(38,144,68,0.1)" : "0 1px 2px rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = "#f2f2f2"; e.currentTarget.style.borderColor = "#c8c8c8"; } }}
                  onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background = "#fafafa"; e.currentTarget.style.borderColor = "#e4e4e4"; } }}
                >
                  <div>
                    <div style={{ fontWeight: "600", fontSize: "13px", color: isSelected ? "#269044" : "#202020" }}>{cat}</div>
                    <div style={{ fontSize: "11px", color: "#9a9a9a", marginTop: "2px" }}>{count} asset{count !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: isSelected ? "#269044" : "#f0f0f0",
                    border: isSelected ? "none" : "1px solid #d3d3d3",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all 0.15s ease"
                  }}>
                    {isSelected && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Middle: Submodels — flex 1 */}
        <div style={{ flex: 1, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Available Submodels</span>
            {categoryToSend && (
              <span style={{ fontSize: "11px", background: "rgba(38,144,68,0.1)", color: "#269044", fontWeight: "600", padding: "2px 8px", borderRadius: "10px" }}>
                {categoryToSend}
              </span>
            )}
          </div>
          <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />

          <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
            {!clicked ? (
              /* Empty state */
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "12px", paddingBottom: "60px" }}>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#d8d8d8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <line x1="9" y1="8" x2="15" y2="8" />
                  <line x1="9" y1="12" x2="15" y2="12" />
                  <line x1="9" y1="16" x2="12" y2="16" />
                </svg>
                <div style={{ fontSize: "13px", fontWeight: "500", color: "#b0b0b0", textAlign: "center", lineHeight: "1.5" }}>
                  Select a category on the left<br />to view its submodels
                </div>
              </div>
            ) : !isFetchCompleted ? (
              <div style={{ display: "flex", justifyContent: "center", paddingTop: "50px" }}>
                <CircularProgress size={36} sx={{ color: "#269044" }} />
              </div>
            ) : details.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: "10px" }}>
                <div style={{ fontSize: "13px", color: "#b0b0b0", fontWeight: "500" }}>No submodels found for this category</div>
              </div>
            ) : (
              details.map((submodel, i) => {
                const isChecked = checkedSubModels[i] || subModels.includes(submodel.idShort);
                const isActive = activeSubmodels.includes(submodel.idShort);
                return (
                  <div
                    key={i}
                    onClick={() => toggleCheckbox({ target: { checked: !isChecked } }, i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 12px",
                      marginBottom: "6px",
                      borderRadius: "8px",
                      border: isChecked ? "1.5px solid rgba(38,144,68,0.4)" : "1px solid #e4e4e4",
                      background: isChecked ? "rgba(38,144,68,0.05)" : "#fafafa",
                      cursor: "pointer",
                      gap: "10px",
                      transition: "all 0.15s ease",
                      boxShadow: isChecked ? "0 1px 4px rgba(38,144,68,0.08)" : "0 1px 2px rgba(0,0,0,0.03)"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => { e.stopPropagation(); toggleCheckbox(e, i); }}
                      onClick={e => e.stopPropagation()}
                      style={{ accentColor: "#269044", width: "15px", height: "15px", cursor: "pointer", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", fontSize: "13px", color: isChecked ? "#269044" : "#202020" }}>{submodel.idShort}</div>
                      {isActive && (
                        <div style={{ fontSize: "10px", color: "#269044", fontWeight: "700", marginTop: "2px", letterSpacing: "0.06em" }}>SYNCING</div>
                      )}
                    </div>
                    {isChecked && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#269044" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Submit — pinned to bottom */}
          {clicked && isFetchCompleted && details.length > 0 && (
            <div style={{ padding: "12px 14px", borderTop: "1px solid #ebebeb", flexShrink: 0 }}>
              <button
                onClick={handleSubmodels}
                style={{ width: "100%", padding: "10px", background: "#269044", color: "#fff", fontWeight: "600", fontSize: "13px", border: "none", borderRadius: "7px", cursor: "pointer", transition: "background 0.15s ease" }}
                onMouseEnter={e => e.currentTarget.style.background = "#1e7438"}
                onMouseLeave={e => e.currentTarget.style.background = "#269044"}
              >
                Save Submodel Selection
              </button>
            </div>
          )}
        </div>

        {/* Right: Guidelines — 42% */}
        <div style={{ width: "42%", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 10px", flexShrink: 0 }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>General Guidelines</span>
          </div>
          <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
            {guidelineData.map((g, i) => {
              const isOpen = openGuidelines.includes(i);
              return (
                <div key={i} style={{ marginBottom: "8px", borderRadius: "9px", border: "1px solid #e4e4e4", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  {/* Accordion header */}
                  <div
                    onClick={() => toggleGuideline(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 14px",
                      cursor: "pointer",
                      background: isOpen ? "rgba(38,144,68,0.04)" : "#fff",
                      transition: "background 0.15s ease",
                      userSelect: "none"
                    }}
                  >
                    <div style={{
                      width: "26px", height: "26px", borderRadius: "7px", flexShrink: 0,
                      background: isOpen ? "#269044" : "#f0f0f0",
                      color: isOpen ? "#fff" : "#7c7c7c",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11px", fontWeight: "700",
                      transition: "all 0.15s ease"
                    }}>{i + 1}</div>
                    <span style={{ flex: 1, fontWeight: "600", fontSize: "13px", color: "#202020", lineHeight: "1.4" }}>{g.title}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#afafaf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ flexShrink: 0, transition: "transform 0.2s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {/* Accordion body */}
                  {isOpen && (
                    <div style={{ padding: "4px 14px 14px 52px", borderTop: "1px solid #f0f0f0" }}>
                      <ul style={{ margin: "10px 0 0 0", padding: "0 0 0 14px" }}>
                        {g.bullets.map((bullet, j) => (
                          <li key={j} style={{ fontSize: "13px", color: "#404040", lineHeight: "1.7", marginBottom: j < g.bullets.length - 1 ? "6px" : 0 }}>
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Filtering;
